import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getMeetingId, type SupportedItem } from './action-item-types';
import { InlineEditableText } from './inline-editable-text';
import { useActionItemMutations } from '../hooks/use-action-item-mutations';

interface EditableDescriptionProps {
  item: SupportedItem;
  meetingId?: string; // Optional - will use item.meetingId if available (ProjectActionItem)
  onUpdate?: (updated: SupportedItem) => void;
}

export interface EditableDescriptionHandle {
  startEditing: () => void;
}

export const EditableDescription = forwardRef<EditableDescriptionHandle, EditableDescriptionProps>(
  ({ item, meetingId: propMeetingId, onUpdate }, ref) => {
    const { t } = useTranslation();
    const resolvedMeetingId = getMeetingId(item, propMeetingId);
    const { updateItem } = useActionItemMutations(resolvedMeetingId);

    const [isEditing, setIsEditing] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        startEditing: () => setIsEditing(true),
      }),
      [],
    );

    const commitEdit = async (descriptionText: string) => {
      try {
        await updateItem(item.id, { descriptionText });
        onUpdate?.({ ...item, descriptionText } as SupportedItem);
        toast.success(t('meeting-secretary.action-items.item-updated'));
      } catch {
        toast.error(t('meeting-secretary.meetings.error-updating-item'));
      }
    };

    if (isEditing) {
      return (
        <InlineEditableText
          initialValue={item.descriptionText ?? ''}
          onCommit={commitEdit}
          onCancel={() => setIsEditing(false)}
        />
      );
    }

    return <p className="text-sm text-gray-900 whitespace-pre-wrap">{item.descriptionText}</p>;
  },
);

EditableDescription.displayName = 'EditableDescription';
